import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { FilterOptions, RefHit, RefKind } from './useFilterOptions'

/** How long typing has to pause before the search goes out. */
const SEARCH_DELAY_MS = 250
const MIN_CHARS = 2
const WORD: Record<RefKind, string> = { organisations: 'organisatie', persons: 'persoon' }

/** The answer to the search for `term` among `kind`; `hits` is null when that search failed. */
interface Answer {
  kind: RefKind
  term: string
  hits: RefHit[] | null
}

/**
 * Picks organisations or persons for a filter (design §5.2): a search field that waits for two
 * characters and a pause, the hits as buttons, and the chosen ones as removable chips. A name comes
 * from what was picked here, else from `names`; an id nobody can name reads "Onbekend".
 *
 * Only the answer to the text in the box, and for the kind asked for now, is listed: an older one
 * would offer hits that do not match what is typed, or organisations where persons are asked for.
 */
export function RefPicker({
  kind,
  label,
  value,
  options,
  onChange,
}: {
  kind: RefKind
  /** The name of the group, e.g. "Organisatie". */
  label: string
  value: readonly string[]
  options: Pick<FilterOptions, 'names' | 'searchRefs' | 'remember'>
  onChange: (next: string[]) => void
}) {
  const [term, setTerm] = useState('')
  const [answer, setAnswer] = useState<Answer | null>(null)
  const [picked, setPicked] = useState<ReadonlyMap<string, string>>(new Map())
  const { names, searchRefs, remember } = options
  const trimmed = term.trim()
  const ready = trimmed.length >= MIN_CHARS

  useEffect(() => {
    if (!ready || !searchRefs) return
    let stale = false
    const timer = setTimeout(() => {
      searchRefs(kind, trimmed).then(
        (hits) => {
          if (!stale) setAnswer({ kind, term: trimmed, hits })
        },
        () => {
          if (!stale) setAnswer({ kind, term: trimmed, hits: null })
        },
      )
    }, SEARCH_DELAY_MS)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [kind, trimmed, ready, searchRefs])

  const current = ready && answer?.kind === kind && answer.term === trimmed ? answer : null
  const hits = (current?.hits ?? []).filter((hit) => !value.includes(hit.id))

  function pick(hit: RefHit) {
    setPicked((previous) => new Map(previous).set(hit.id, hit.name))
    remember?.(kind, hit.id, hit.name)
    onChange([...value, hit.id])
    setTerm('')
  }

  return (
    <div role="group" aria-label={label} className="flex flex-col gap-2">
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((id) => {
            const name = picked.get(id) ?? names.get(id) ?? 'Onbekend'
            return (
              <li key={id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted py-0.5 pr-1 pl-3 text-sm">
                <span className="truncate">{name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`${name} weghalen`}
                  onClick={() => onChange(value.filter((chosen) => chosen !== id))}
                >
                  <X />
                </Button>
              </li>
            )
          })}
        </ul>
      ) : null}
      <Input
        type="search"
        maxLength={200}
        aria-label={`Zoek ${WORD[kind]}`}
        placeholder="Zoeken…"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        className="max-md:h-11 max-md:text-base"
      />
      {!ready ? (
        <p className="px-1 text-sm text-muted-foreground">Typ minstens {MIN_CHARS} tekens om te zoeken.</p>
      ) : !searchRefs ? null : current === null ? (
        <p role="status" className="px-1 text-sm text-muted-foreground">
          Zoeken…
        </p>
      ) : current.hits === null ? (
        <p role="alert" className="px-1 text-sm text-destructive">
          Zoeken mislukt. Probeer het opnieuw.
        </p>
      ) : hits.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">Geen resultaten</p>
      ) : (
        <ul className="flex max-h-48 flex-col overflow-y-auto">
          {hits.map((hit) => (
            <li key={hit.id}>
              <button
                type="button"
                onClick={() => pick(hit)}
                className="flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm hover:bg-muted max-md:min-h-11"
              >
                <span className="min-w-0 break-words">{hit.name}</span>
                {hit.detail ? <span className="shrink-0 text-muted-foreground">· {hit.detail}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
